import { CheckCircleFilled, LoadingOutlined, SendOutlined } from "@ant-design/icons";
import { Alert, Button, Form, Input, Popconfirm, Space, Typography } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  type SetupPortal as Portal,
  useMarkBaaSignedOfflineMutation,
  useRefreshBaaStatusMutation,
  useResendBaaMutation,
  useSetBaaRecipientMutation,
} from "../../service/onboarding.service";

const { Text } = Typography;

type Props = {
  token: string;
  portal: Portal;
  /** True when DocuSign has bounced the customer back to us after signing. */
  justReturned: boolean;
  onReturnHandled: () => void;
};

/** Step 1 — issue and sign the BAA. */
export default function BaaStep({ token, portal, justReturned, onReturnHandled }: Props) {
  const [setRecipient, { isLoading: isSending }] = useSetBaaRecipientMutation();
  const [resend, { isLoading: isResending }] = useResendBaaMutation();
  const [refreshStatus, { isLoading: isRefreshing }] = useRefreshBaaStatusMutation();
  const [markOffline, { isLoading: isMarkingOffline }] = useMarkBaaSignedOfflineMutation();
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const handledReturnRef = useRef(false);

  const { baa } = portal;
  const isSigned = !!baa.signed_at;
  const awaitingNudge = !isSigned && !!baa.customer_signed_at;
  const isSent = !isSigned && !awaitingNudge && !!baa.sent_at;

  // DocuSign can bounce a signer back here; ask DocuSign rather than wait on Connect.
  useEffect(() => {
    if (!justReturned || handledReturnRef.current) return;
    handledReturnRef.current = true;
    (async () => {
      try {
        await refreshStatus({ token }).unwrap();
      } catch {
        // The webhook remains the backstop.
      } finally {
        onReturnHandled();
      }
    })();
  }, [justReturned, token, refreshStatus, onReturnHandled]);

  const send = useCallback(
    async (values: { email: string; cc_emails?: (string | undefined)[] }) => {
      setError(null);
      setNotice(null);
      try {
        // Form.List leaves holes when a row is removed, so blanks are dropped here.
        const cc = (values.cc_emails ?? []).map((v) => (v ?? "").trim()).filter(Boolean);
        await setRecipient({ token, email: values.email, cc_emails: cc }).unwrap();
        setIsEditing(false);
      } catch (err) {
        const code = (err as { data?: { code?: string } })?.data?.code;
        if (code === "not_configured") {
          setError("We're still preparing your BAA — we'll send it shortly.");
          return;
        }
        setError("We couldn't send the agreement just now. Please try again in a moment.");
      }
    },
    [setRecipient, token],
  );

  const handleResend = useCallback(async () => {
    setError(null);
    try {
      await resend({ token }).unwrap();
      setNotice("Sent again — it can take a minute to arrive, and it may land in spam.");
    } catch {
      setError("We couldn't resend it just now. Please try again in a moment.");
    }
  }, [resend, token]);

  const handleCheck = useCallback(async () => {
    setError(null);
    setNotice(null);
    try {
      const updated = await refreshStatus({ token }).unwrap();
      if (!updated.baa.signed_at && !updated.baa.customer_signed_at) {
        setNotice("DocuSign hasn't recorded a signature yet. This updates on its own the moment they sign.");
      }
    } catch {
      setError("We couldn't reach DocuSign just now. Please try again in a moment.");
    }
  }, [refreshStatus, token]);

  const handleMarkOffline = async () => {
    setError(null);
    setNotice(null);
    try {
      await markOffline({ token }).unwrap();
    } catch {
      setError("We couldn't record that just now. Please try again in a moment.");
    }
  };

  // While e-sign isn't live the agreement is exchanged by email; this closes the step.
  const offlineAction = baa.offline_allowed ? (
    <Popconfirm
      title="Signed by email?"
      description="Confirm the signed BAA has been sent back to Nudge by email. We'll mark this step complete."
      okText="Yes, mark it done"
      cancelText="Not yet"
      onConfirm={handleMarkOffline}
    >
      <Button type="link" loading={isMarkingOffline} style={{ paddingLeft: 0 }}>
        Signed it by email instead? Mark as done
      </Button>
    </Popconfirm>
  ) : null;

  if (isSigned) {
    return (
      <Alert
        type="success"
        showIcon
        icon={<CheckCircleFilled />}
        message={
          baa.signed_offline
            ? "Your BAA was signed by email and is on file. Nothing else needed here."
            : "Your BAA is signed by both sides and on file. Nothing else needed here."
        }
      />
    );
  }

  if (awaitingNudge) {
    return (
      <Alert
        type="info"
        showIcon
        icon={<LoadingOutlined />}
        message={`Signed by ${baa.signer_name || "your signer"} — thank you.`}
        description={`It's with ${baa.countersigner_name} to counter-sign. This step completes on its own, and we'll email you when it does.`}
      />
    );
  }

  if (isSent && !isEditing) {
    return (
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        {error ? <Alert type="warning" showIcon message={error} /> : null}
        {notice ? <Alert type="info" showIcon message={notice} /> : null}
        <Alert
          type="warning"
          showIcon
          message={
            baa.signer_name ? `Sent to ${baa.signer_name} at ${baa.signer_email}` : `Sent to ${baa.signer_email}`
          }
          description="They'll get an email from DocuSign with the agreement. We've already signed on our side, so this step completes as soon as they do."
        />
        <Space wrap>
          <Button loading={isRefreshing} onClick={handleCheck}>
            Check for an update
          </Button>
          <Button type="link" loading={isResending} onClick={handleResend}>
            Resend the email
          </Button>
          <Button type="link" onClick={() => setIsEditing(true)}>
            Wrong person or address?
          </Button>
        </Space>
        {offlineAction}
      </Space>
    );
  }

  return (
    <Space direction="vertical" size={4} style={{ width: "100%" }}>
      {error ? <Alert type="warning" showIcon message={error} style={{ marginBottom: 8 }} /> : null}

      {isEditing ? (
        <Text type="secondary" style={{ fontSize: 13 }}>
          We'll withdraw the agreement we already sent and issue a fresh one to the address you enter below.
        </Text>
      ) : null}

      <Form
        layout="vertical"
        requiredMark={false}
        initialValues={{ email: baa.suggested_email, cc_emails: baa.cc_emails ?? [] }}
        onFinish={send}
        style={{ marginTop: 12 }}
      >
        <Form.Item
          name="email"
          label="Who signs it?"
          extra="Enter their email — we'll send the agreement straight there."
          rules={[
            { required: true, message: "We need an address to send it to." },
            { type: "email", message: "That doesn't look like an email address." },
          ]}
        >
          <Input placeholder="name@yourpractice.com" autoComplete="off" inputMode="email" />
        </Form.Item>

        <Form.List name="cc_emails">
          {(fields, { add, remove }) => (
            <Form.Item
              label="Anyone else who should receive it (optional)"
              extra="They get the agreement and the signed copy, but only the address above can sign it."
              style={{ marginBottom: 12 }}
            >
              {fields.map((field) => (
                <Space key={field.key} align="baseline" style={{ display: "flex", marginBottom: 8 }}>
                  <Form.Item
                    {...field}
                    noStyle
                    rules={[{ type: "email", message: "That doesn't look like an email address." }]}
                  >
                    <Input
                      placeholder="colleague@yourpractice.com"
                      autoComplete="off"
                      inputMode="email"
                      style={{ minWidth: 260 }}
                    />
                  </Form.Item>
                  <Button type="text" onClick={() => remove(field.name)}>
                    Remove
                  </Button>
                </Space>
              ))}
              {fields.length < 10 ? (
                <Button type="link" onClick={() => add()} style={{ paddingLeft: 0 }}>
                  + Add another recipient
                </Button>
              ) : null}
            </Form.Item>
          )}
        </Form.List>
        <Space wrap>
          <Button type="primary" htmlType="submit" icon={<SendOutlined />} loading={isSending}>
            {isEditing ? "Re-issue the BAA" : "Send the BAA"}
          </Button>
          {isEditing ? (
            <Button type="link" onClick={() => setIsEditing(false)}>
              Cancel
            </Button>
          ) : null}
        </Space>
      </Form>

      <Text type="secondary" style={{ fontSize: 12, marginTop: 10, display: "block" }}>
        {baa.countersigner_name} counter-signs after them, and this step completes on its own — you don't need to tell
        us.
      </Text>
      {offlineAction}
    </Space>
  );
}
